import { useEffect, useState } from "react";
import { Box, CircularProgress, Stack, Typography } from "@mui/material";
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from "recharts";

import { CardLayout, CustomTooltip, EmptyState } from "../v3";
import { AnalyticsService } from "../../services/analytics.service";

export const OfficeBudgetOverview = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [colors, setColors] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  const generateShades = (count) => {
    const baseHue = 255;
    const baseSaturation = 70;
    const baseLightness = 80;
    const shades = [];

    for (let i = 0; i < count; i++) {
      const saturation = Math.min(baseSaturation + i * 5, 100);
      const lightness = Math.max(baseLightness - i * 10, 35);
      shades.push(`hsl(${baseHue}, ${saturation}%, ${lightness}%)`);
    }

    return shades.reverse();
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const { data: response } = await AnalyticsService.getCategorySummary({ page: 1, limit: 100, isOffice: true });

      const totalExpense = response.reduce((sum, item) => sum + parseFloat(item.totalExpense), 0);
      const formattedData = [];
      let otherExpenseTotal = 0;
      const legendNameMap = {
        WORKER_EXPENSE: "Worker",
        SUBCONTRACTOR_EXPENSE: "Subcontractor",
        EMPLOYEE_EXPENSE: "Employee",
        VENDOR_EXPENSE: "Materials",
        OTHER_EXPENSE: "Other Expense",
      };

      response.forEach((item) => {
        const expense = parseFloat(item.totalExpense);
        const normalizedExpenseRatio = Math.max(expense / totalExpense, 0.02);

        if (["WORKER_EXPENSE", "SUBCONTRACTOR_EXPENSE", "EMPLOYEE_EXPENSE", "VENDOR_EXPENSE"].includes(item.categoryName)) {
          formattedData.push({ name: legendNameMap[item.categoryName], value: normalizedExpenseRatio, expense });
        } else {
          otherExpenseTotal += expense;
        }
      });

      if (otherExpenseTotal > 0) {
        const normalizedOtherExpenseRatio = Math.max(otherExpenseTotal / totalExpense, 0.02);
        formattedData.push({ name: legendNameMap.OTHER_EXPENSE, value: normalizedOtherExpenseRatio, expense: otherExpenseTotal });
      }

      formattedData.sort((a, b) => b.expense - a.expense);

      setData(formattedData);
      setTotal(totalExpense);
      setColors(generateShades(formattedData.length));
    } catch (error) {
      console.error("Error fetching office budget data:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <CardLayout sx={{ margin: "11px" }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: 600,
            color: "#674BF5",
            textAlign: "left",
          }}
        >
          Budget Overview
        </Typography>
      </Box>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress size={32} sx={{ color: "#674BF5" }} />
        </Box>
      ) : data?.length > 0 ? (
        <>
          <Box sx={{ display: "flex", justifyContent: "center", gap: 8, alignItems: "center" }}>
            <Box
              sx={{
                position: "relative",
                width: 200,
                height: 200,
              }}
            >
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={data}
                    startAngle={225}
                    endAngle={-45}
                    cx="50%"
                    cy="50%"
                    innerRadius={70}
                    outerRadius={95}
                    dataKey="value"
                  >
                    {data.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={colors[index]} stroke="none" />
                    ))}
                  </Pie>

                  <Tooltip content={<PieChartTooltip />} cursor={false} wrapperStyle={{ zIndex: 9999, overflow: "visible" }} />
                </PieChart>
              </ResponsiveContainer>

              <CustomTooltip title={`Overall Total: ${formatAmount(total)}`}>
                <Box
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    textAlign: "center",
                    cursor: "pointer",
                  }}
                >
                  <Typography sx={{ fontWeight: 600, textTransform: "uppercase" }}>{formatAmount(total)}</Typography>
                  <Typography variant="caption">Overall</Typography>
                </Box>
              </CustomTooltip>
            </Box>

            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
              }}
            >
              <Stack spacing={2}>
                {data.map((item, index) => (
                  <Box key={item.name} display="flex" alignItems="center" gap={1.5}>
                    <Box
                      sx={{
                        width: 14,
                        height: 14,
                        borderRadius: "50%",
                        backgroundColor: colors[index],
                      }}
                    />
                    <Typography variant="body2" sx={{ fontWeight: 600, textTransform: "capitalize" }}>
                      {`${item.name} (${formatAmount(item.expense)})`}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </Box>
          </Box>
        </>
      ) : (
        <EmptyState title="No budget data found" />
      )}
    </CardLayout>
  );
};

const PieChartTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    const { name, expense } = payload[0].payload;
    return (
      <Box
        sx={{
          backgroundColor: "#fff",
          border: "1px solid #E0E0E0",
          borderRadius: 1,
          p: 1,
          boxShadow: "0px 2px 8px rgba(0,0,0,0.15)",
          pointerEvents: "none",
          zIndex: 9999,
        }}
      >
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {name}
        </Typography>
        <Typography variant="body2">{formatAmount(expense)}</Typography>
      </Box>
    );
  }
  return null;
};

const formatAmount = (value) => {
  if (value >= 10000000) return `₹ ${(value / 10000000).toFixed(1)}Cr`;
  if (value >= 100000) return `₹ ${(value / 100000).toFixed(1)}L`;
  if (value >= 1000) return `₹ ${(value / 1000).toFixed(1)}K`;
  return `₹ ${value}`;
};
